import {udApi} from '@udemy/ud-api';

import {
    LearningReminderCalendarType,
    LearningReminderNotificationMethod,
    useLearningReminderCreateMutation,
} from 'src/generated/graphql';

import {toMonolithTime} from './calendar-export';

import type {ReminderRecord} from './calendar-export';
import type {GoogleTokenResponse} from './google-calendar-auth-api';

interface CreateLearningReminderParams {
    title: string;
    start: Date;
    end: Date;
    courseId?: string;
    courseUrl?: string;
}

export async function createLearningReminder({
    title,
    start,
    end,
    courseId,
    courseUrl,
}: CreateLearningReminderParams): Promise<ReminderRecord> {
    const gqlResult = await useLearningReminderCreateMutation.fetcher({
        learningReminder: {
            title,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            durationInMinutes: Math.round((end.getTime() - start.getTime()) / (60 * 1000)),
            calendarType: LearningReminderCalendarType.Other,
            recurrencePattern: '',
            reminderMethod: LearningReminderNotificationMethod.Email,
            reminderMinutesBefore: 0,
            learningProduct: courseId ? {id: courseId, type: 'COURSE'} : null,
        },
    })();

    const serviceLearningReminderId = gqlResult.learningReminderCreate?.learningReminder?.id;

    const response = await udApi.post('/users/me/learning-calendar-reminders/', {
        title,
        start_time: toMonolithTime(start),
        end_time: toMonolithTime(end),
        frequency: 'once',
        calendar_type: 'other',
        notification_method: 'email',
        notification_time_before: 0,
        service_learning_reminder_id: serviceLearningReminderId,
        sequence: 0,
        ...(courseId && {content_type: 'course', object_id: Number(courseId)}),
    });

    return {...response.data, courseUrl};
}

export async function syncReminderToGoogleCalendar(
    reminder: ReminderRecord,
    tokenResponse: GoogleTokenResponse,
): Promise<void> {
    await udApi.patch(`/users/me/learning-calendar-reminders/${reminder.id}/`, {
        title: reminder.title,
        start_time: reminder.start_time,
        end_time: reminder.end_time,
        frequency: 'once',
        calendar_type: 'google',
        notification_method: 'email',
        notification_time_before: 0,
        access_token: tokenResponse.access_token,
        // expires_at is semantically correct but currently backend-inert — sent for forward-compatibility if that changes.
        expires_at: Math.floor(Date.now() / 1000) + tokenResponse.expires_in,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
}
